// packages/core/src/learning/school-modules/first-100k.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "First $100k". surface: 'school', homeSchool: 'first-100k'.
// module id 'school-first-100k' matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #0f7a6b) — flag for the presentation-tuning pass.
// tier/pillar: early-accumulation / freedom lens → tier 'freedom-path',
// pillar 'freedom-math' (consistent with the other freedom-lifestyle-paths
// bucket schools authored so far).
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_FIRST_100K_MODULE: LearningModule = {
	id: 'school-first-100k',
	title: 'First $100k',
	description:
		'The lens that treats the first serious money base as proof your system works — built by protecting the gap through the slow, boring early stretch.',
	emoji: '💰', // PLACEHOLDER — tune in presentation pass
	accentColor: '#0f7a6b', // from SchoolMeta color — tune in presentation pass
	tier: 'freedom-path',
	pillar: 'freedom-math',
	order: 0,
	surface: 'school',
	homeSchool: 'first-100k',
	lessons: [
		{
			id: 'school-first-100k-01',
			title: 'The first real pile changes you',
			description:
				'This lesson teaches why the first $100,000 is about belief, behavior, and proof more than status.',
			pillar: 'freedom-math',
			schoolsOfThought: ['first-100k'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['early-proof', 'money-confidence', 'foundation-effect'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['early proof', 'money confidence', 'foundation effect'],
			blocks: [
				{
					type: 'paragraph',
					text: 'The first $100,000 gets talked about like it is a magic number. It is not magic. It matters because it can change what you believe is possible.',
				},
				{
					type: 'paragraph',
					text: 'First $100k sees the early stage as the hardest emotional stretch. You are saving, learning, paying attention, and making tradeoffs, but the number may still look small for a long time.',
				},
				{
					type: 'bullet_list',
					items: [
						'The base starts to feel real',
						'Your habits get tested',
						'Your future stops feeling imaginary',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating the milestone like a flex instead of a foundation.',
				},
				{
					type: 'example',
					text: 'Someone with $3,000 saved may still feel like they are nowhere. Someone with $70,000 may not be free yet, but they may finally believe their system is working.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that a number can become a scoreboard. If the milestone turns into shame, comparison, or identity, it stops helping. Different people have different incomes, costs, families, debts, luck, and starting points.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to your Dashboard. You are not asking whether you are winning against strangers. You are asking whether your current base is growing into real options.',
				},
				{
					type: 'key_takeaway',
					text: 'The first $100,000 matters because it proves the path can become real.',
				},
			],
			audioScript:
				'The first $100,000 gets treated like a legend. People repeat it because it sounds clean, simple, and dramatic. But the useful point is not that $100,000 is a magic doorway where life suddenly becomes easy. It is not. Bills still exist. Rent still exists. Markets still move. Life still happens.\n\nThe real reason the first $100,000 matters is that it can change your belief system.\n\nBefore you have a real base, money building can feel fake. You save a few hundred dollars, then something breaks. You invest a little, then the market drops. You get a raise, then rent goes up. You try to be responsible, but the number still looks unimpressive. That early phase can feel insulting because the effort is real and the result looks small.\n\nFirst $100k pays attention to that emotional stretch. It says the first serious pile is not only about dollars. It is about proof. Proof that you can keep money. Proof that you can build a gap between income and spending. Proof that your habits can survive boredom. Proof that your future self is not just a motivational quote.\n\nA person with $5,000 saved may still feel one emergency away from panic. A person with $50,000 may still have a long way to go, but something begins to shift. They have evidence. They have a base. They have a reason to keep going. By the time someone reaches a serious milestone, they usually have more than money. They have learned how to say no, how to wait, how to recover from mistakes, how to watch lifestyle creep, and how to keep showing up.\n\nThat is why the first $100,000 can feel different. It is not just a number. It is a mirror that says, this system is no longer theoretical.\n\nBut there is a trap. The milestone can become a scoreboard. People can start comparing ages, salaries, portfolios, inheritances, and timelines. That is a losing game because everyone starts from a different place. Some people support family. Some live in expensive cities. Some had debt. Some had help. Some had luck. Some had setbacks that never show up in a clean money post.\n\nThe healthy version of this lens is not, reach this number or you failed. The healthy version is, build proof that your money system can work.\n\nInside Nisk, you would look at your Dashboard and ask where your base is today. Not to shame yourself. Not to pretend the number tells your whole story. Just to see the foundation. The first real pile matters because it changes the way you see your own power.',
			flashcards: [
				{
					id: 'school-first-100k-01-fc-1',
					role: 'main_idea',
					front: 'Why does the first $100k matter?',
					back: 'It proves your system can work and changes your confidence.',
				},
				{
					id: 'school-first-100k-01-fc-2',
					role: 'watch_out',
					front: 'What is the milestone trap?',
					back: 'Turning the number into shame, comparison, or status.',
				},
				{
					id: 'school-first-100k-01-fc-3',
					role: 'use_it',
					front: 'How do you use this lens?',
					back: 'Track whether your base is growing into real options.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Look at your current base',
			},
			revision: 1,
		},
		{
			id: 'school-first-100k-02',
			title: 'Momentum starts before it looks big',
			description:
				'This lesson teaches why the first stage feels slow and why First $100k cares about staying with the process.',
			pillar: 'freedom-math',
			schoolsOfThought: ['first-100k'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['invisible-momentum', 'boring-phase', 'consistency'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'invisible momentum',
				'boring phase',
				'consistency pressure',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Early progress can feel almost disrespectful. You save, cut back, earn more, and the number still moves slowly. First $100k is built for that boring middle.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes momentum often starts before it looks impressive. The early stage is where you build the habits, gap, and patience that later make the number move with more force.',
				},
				{
					type: 'bullet_list',
					items: [
						'Small deposits train consistency',
						'Repeated choices build identity',
						'A growing base makes future growth feel possible',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is quitting because the first stage does not feel rewarding enough.',
				},
				{
					type: 'example',
					text: 'Saving $300 a month may not look dramatic at first. But the person who keeps going is also building the muscle to save $500, then $800, then more when income rises.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that patience can sound easy from the outside. If your income is low or your costs are heavy, momentum may need to start with stability, not aggressive saving.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens belongs in savings rate testing. You are asking what your current monthly gap can become if it keeps showing up.',
				},
				{
					type: 'key_takeaway',
					text: 'The boring phase is not wasted. It is where momentum learns how to exist.',
				},
			],
			audioScript:
				'One of the hardest parts of building the first $100,000 is that early progress can look tiny. You can do the right things and still feel behind. You can save for months and still not feel secure. You can skip purchases, cook more, work extra, cancel things, and the number may still look unimpressive.\n\nThat is why First $100k is such a useful lens. It respects the boring phase.\n\nThe boring phase is the part where you are not rich, not free, not impressive, and not done. You are just building. You might be saving $100 at a time. You might be paying down old mistakes. You might be rebuilding after debt, a bad job, a move, a breakup, or years of not paying attention. It does not feel glamorous. It often feels slow.\n\nBut this lens says momentum starts before it looks big.\n\nAt first, the money is only part of the win. The bigger win is that you are becoming someone who keeps a gap. You are learning how to not spend every improvement. You are learning how to recover after an expensive month. You are learning how to look at your numbers without avoiding them. You are learning how to make a plan and come back after you mess up.\n\nThat behavior matters because later, when income rises, the same habits can carry more weight. A person who learns to save $200 a month may later save $500. A person who learns to keep part of a raise may later keep a larger part. A person who learns not to panic when progress is slow is harder to knock off course.\n\nBut this idea has to stay grounded. Some people hear “just be patient” from people who had higher income, cheaper housing, fewer obligations, or family support. That can feel insulting. If your base is fragile, the first goal may not be speed. It may be stability. Keeping $500 from disappearing matters. Keeping one month of bills matters. Getting out of survival mode matters.\n\nFirst $100k is not saying the first stage is easy. It is saying the first stage is where the system is built.\n\nInside Nisk, you would use savings rate and simulation to see what your monthly gap is doing. Maybe your gap is small today. Fine. What happens if it grows by $100? What happens if a raise does not get fully absorbed? What happens if your system gets more consistent?\n\nThe boring phase is where you earn trust with yourself. It may not look big yet, but it is not nothing.',
			flashcards: [
				{
					id: 'school-first-100k-02-fc-1',
					role: 'main_idea',
					front: 'When does momentum start?',
					back: 'Before the number looks impressive, while habits are forming.',
				},
				{
					id: 'school-first-100k-02-fc-2',
					role: 'watch_out',
					front: 'Why do people quit early?',
					back: 'The early stage feels slow and unrewarding.',
				},
				{
					id: 'school-first-100k-02-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'Test how your monthly gap can grow over time.',
				},
			],
			applyAction: {
				type: 'analyzeSavingsRate',
				label: 'Check your monthly gap',
			},
			revision: 1,
		},
		{
			id: 'school-first-100k-03',
			title: 'Your gap becomes your engine',
			description:
				'This lesson teaches how First $100k interprets the gap between income and spending as the engine of early accumulation.',
			pillar: 'freedom-math',
			schoolsOfThought: ['first-100k'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'accumulation-engine',
				'income-spending-gap',
				'raise-capture',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'accumulation engine',
				'income spending gap',
				'raise capture',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'The first $100,000 usually does not come from one heroic move. It comes from a gap that keeps showing up.',
				},
				{
					type: 'paragraph',
					text: 'First $100k sees the gap between income and spending as the engine. Income helps. Spending control helps. But the part that builds the base is what survives after life gets paid for.',
				},
				{
					type: 'bullet_list',
					items: [
						'Earn more when possible',
						'Keep fixed costs from swallowing everything',
						'Capture part of each improvement',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is improving income while letting the whole improvement disappear into a bigger lifestyle.',
				},
				{
					type: 'example',
					text: 'If a raise adds $400 a month and $400 goes straight into new expenses, the engine did not grow. If even $200 stays in the gap, the first $100,000 gets closer.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can overfocus on personal behavior. Wages, rent, family obligations, healthcare, and location still matter. The gap is not always easy to grow.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, use Simulate to test how changes in the gap affect the climb. The question is not what you should do. The question is what each change does to the path.',
				},
				{
					type: 'key_takeaway',
					text: 'Your first $100,000 is built by the gap you protect, not the income you only display.',
				},
			],
			audioScript:
				'First $100k cares about the gap. That sounds simple, but it is one of the most important ideas in the whole lens.\n\nThe gap is what remains between income and spending. Not your salary. Not your job title. Not what people think you make. The gap. That is the part that can build the base.\n\nA person can make good money and still have no engine if the whole paycheck disappears. Another person can make less, but if they consistently keep part of what comes in, they are building something. It may be slower. It may be frustrating. But the engine exists.\n\nThis is why the first $100,000 lens pays attention to raises. A raise is powerful, but only if part of it survives. If your income goes up by $400 a month and your spending quietly goes up by $400 too, your lifestyle changed, but your future may not have. You may feel richer and still be just as dependent on the next paycheck.\n\nThat is the trap. Progress gets absorbed before it becomes a foundation.\n\nThe mature move is not to reject every upgrade. Life is for living. The mature move is to capture part of improvement. Maybe you get a raise and improve your life a little, but not so much that the whole raise disappears. Maybe you lower one fixed cost and keep the difference. Maybe you add side income and let some of it build the base instead of treating all of it as bonus spending.\n\nOver time, that gap becomes your engine.\n\nBut this lens has to be fair. Not everyone has the same ability to grow the gap. A person paying high rent, supporting relatives, dealing with medical costs, or working unstable hours may not have the same room as someone with a high salary and low obligations. First $100k should not pretend structure does not exist. It should not shame people whose gap is small.\n\nInstead, it asks a practical question: what part of your current situation can become more powerful? Is there a way to raise income over time? Is there a cost that can stop growing? Is there a habit that keeps eating every improvement? Is there a small gap you can protect until it gets stronger?\n\nInside Nisk, this becomes a simulation question. What happens if the gap grows by $100 a month? What about $250? What if the raise is partly captured instead of fully spent? The answer is not personal advice. It is a tradeoff map.\n\nThe first $100,000 is rarely built by looking rich. It is built by protecting the engine before the engine looks impressive.',
			flashcards: [
				{
					id: 'school-first-100k-03-fc-1',
					role: 'main_idea',
					front: 'What builds the first $100k?',
					back: 'The gap between income and spending that keeps showing up.',
				},
				{
					id: 'school-first-100k-03-fc-2',
					role: 'watch_out',
					front: 'What kills the engine?',
					back: 'Letting every raise disappear into higher spending.',
				},
				{
					id: 'school-first-100k-03-fc-3',
					role: 'use_it',
					front: 'How do you apply it?',
					back: 'Test what happens when part of each improvement is kept.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate a bigger gap',
				scenarioType: 'aggressive-savings',
			},
			revision: 1,
		},
		{
			id: 'school-first-100k-04',
			title: 'The milestone trains your identity',
			description:
				'This lesson teaches how pursuing the first $100,000 can reshape self trust, discipline, and money identity.',
			pillar: 'freedom-math',
			schoolsOfThought: ['first-100k'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['self-trust', 'money-identity', 'disciplined-flexibility'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'self trust',
				'money identity',
				'disciplined flexibility',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'The first $100,000 is not only a money target. It is an identity test. You find out whether you can keep promises to your future self while real life keeps interrupting.',
				},
				{
					type: 'paragraph',
					text: 'First $100k believes the climb trains self trust. You learn how you behave when progress is slow, when friends spend, when setbacks hit, and when nobody is clapping.',
				},
				{
					type: 'bullet_list',
					items: [
						'You practice patience',
						'You recover from mistakes',
						'You stop needing every win to be visible',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking discipline means never enjoying life.',
				},
				{
					type: 'example',
					text: 'Someone who saves steadily but still plans one meaningful trip may last longer than someone who cuts everything, burns out, and quits the whole plan.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that identity can become rigid. If you become the person who never spends, never rests, and never adapts, the milestone starts owning you.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens can become a reflection. What kind of person are you becoming while you build the base?',
				},
				{
					type: 'key_takeaway',
					text: 'The first $100,000 trains self trust, but it should not turn you into a machine.',
				},
			],
			audioScript:
				'The first $100,000 is not only about money. It is also about identity.\n\nThat might sound dramatic, but think about what the climb actually asks from you. It asks you to keep going when the number is not exciting. It asks you to make choices that other people may not understand. It asks you to recover from expensive months without giving up. It asks you to stop spending every good thing that happens to you. It asks you to believe that future options are worth protecting, even when present pressure is loud.\n\nThat is why this milestone can train self trust.\n\nSelf trust is not built by saying, I am disciplined now. It is built through repeated evidence. You said you would save part of the raise, and you did. You said you would not let one bad month destroy the plan, and you came back. You said you would look at your numbers every week, and you did. You said you would stop comparing your life to people online, and you kept returning to your own path.\n\nOver time, that changes how you see yourself. You are no longer someone hoping money will magically work out. You are someone building proof.\n\nBut there is a trap here. Discipline can become too rigid. Some people turn the first $100,000 into a personality. They stop spending on anything joyful. They treat rest like weakness. They judge friends. They fear every purchase. They become proud of deprivation, even when it starts making their life smaller.\n\nThat is not the goal.\n\nThe better version is disciplined flexibility. You care about the milestone, but you do not worship it. You build the base, but you still live. You say no to things that do not matter, so you can say yes to things that do. You protect the gap, but you do not pretend every dollar spent is failure.\n\nSomeone who saves aggressively for three months, burns out, and quits may be less effective than someone who saves steadily for years with a plan that leaves room for being human. Sustainability matters because the first $100,000 is not reached in one burst for most people. It is reached through a system you can repeat.\n\nInside Nisk, this lesson is less about navigation and more about reflection. What kind of money identity are you building? Are you becoming calmer, more capable, and more honest? Or are you becoming scared, rigid, and obsessed?\n\nThe milestone should build self trust. It should not turn you into a machine.',
			flashcards: [
				{
					id: 'school-first-100k-04-fc-1',
					role: 'main_idea',
					front: 'What does the milestone train?',
					back: 'Self trust, patience, recovery, and money identity.',
				},
				{
					id: 'school-first-100k-04-fc-2',
					role: 'watch_out',
					front: 'What is rigid identity?',
					back: 'Becoming so disciplined that money starts shrinking your life.',
				},
				{
					id: 'school-first-100k-04-fc-3',
					role: 'use_it',
					front: 'What should you ask?',
					back: 'Ask who you are becoming while building the base.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Check your money identity',
				prompt: 'Write the kind of person you want to become while building your first serious money base.',
			},
			revision: 1,
		},
		{
			id: 'school-first-100k-05',
			title: 'The first $100k is not the finish',
			description:
				'This lesson teaches where the First $100k lens breaks down and how to keep the milestone in perspective.',
			pillar: 'freedom-math',
			schoolsOfThought: ['first-100k'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['milestone-humility', 'comparison-trap', 'next-chapter'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['milestone humility', 'comparison trap', 'next chapter'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Reaching the first $100,000 can be powerful. It can also disappoint you if you expected it to fix everything.',
				},
				{
					type: 'paragraph',
					text: 'First $100k is strongest when it treats the number as a foundation. It breaks down when the milestone becomes the whole identity, the whole plan, or the only proof that you are doing well.',
				},
				{
					type: 'bullet_list',
					items: [
						'The number does not remove uncertainty',
						'The number does not create meaning',
						'The number does not make comparison useful',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is believing one milestone will finally make you feel safe forever.',
				},
				{
					type: 'example',
					text: 'Someone may reach $100,000 and still feel anxious about work, family, housing, health, or markets. The milestone helped, but it did not answer every life question.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is emotional. Milestones motivate because they are concrete, but real freedom is bigger than one account balance.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, use the milestone as a checkpoint. Look at what changed, what still feels fragile, and what kind of next chapter the base can support.',
				},
				{
					type: 'key_takeaway',
					text: 'The first $100,000 is a foundation, not a final identity.',
				},
			],
			audioScript:
				'The first $100,000 can feel like a huge milestone. For many people, it is. It may represent years of work, discipline, mistakes, recovery, and patience. It can prove that you are not stuck in the same old cycle. It can give you confidence that your money system has teeth.\n\nBut it is not the finish.\n\nThat is the part people need to hear. If you expect one number to finally make you feel safe forever, you may be disappointed. You can reach $100,000 and still worry about your job. You can reach it and still have family obligations. You can reach it and still wonder whether you are in the right city, career, relationship, or lifestyle. You can reach it and still feel market anxiety. You can reach it and still not know what you want next.\n\nThat does not mean the milestone failed. It means the milestone was never supposed to be your whole life.\n\nFirst $100k is useful because it gives you a clear early target. Humans need targets. A target can turn vague ambition into behavior. It can help you delay upgrades, protect raises, save consistently, and build proof. But if the target becomes your identity, it can start creating pressure instead of freedom.\n\nThis is where comparison gets nasty. Someone reaches $100,000 at twenty five. Someone else reaches it at forty. Someone else is starting over after debt, divorce, medical bills, job loss, family support, or years of not knowing what to do. The milestone is not the same story for everyone. Comparing timelines without comparing context is a fast way to poison something useful.\n\nThe mature version of this lens says: use the milestone as a checkpoint. Ask what changed. Do you feel less fragile? Do you have more runway? Are you less dependent on bad work? Are your habits stronger? Are you clearer about what matters? Are you building options, or only chasing a number?\n\nThen ask what comes next. Maybe the next chapter is more investing. Maybe it is more runway. Maybe it is career risk. Maybe it is a move. Maybe it is helping family from a stronger place. Maybe it is slowing down a little because the base gave you breathing room.\n\nInside Nisk, the first $100,000 should not be treated like a trophy case. It should be treated like a foundation. You stand on it, learn from it, and decide what kind of life it helps you build next.',
			flashcards: [
				{
					id: 'school-first-100k-05-fc-1',
					role: 'main_idea',
					front: 'Is $100k the finish?',
					back: 'No. It is a foundation and checkpoint, not a final identity.',
				},
				{
					id: 'school-first-100k-05-fc-2',
					role: 'watch_out',
					front: 'What is the comparison trap?',
					back: 'Comparing timelines without comparing context, help, income, or obligations.',
				},
				{
					id: 'school-first-100k-05-fc-3',
					role: 'use_it',
					front: 'What comes after it?',
					back: 'Ask what options the base creates and what still feels fragile.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Name your next chapter',
				prompt: 'Write what the first $100,000 would support after the number itself is reached.',
			},
			revision: 1,
		},
	],
};
